// Connection-state tracker. Emits "online" | "syncing" | "offline".
import { useEffect, useState } from "react";

export type ConnState = "online" | "syncing" | "offline";

type Listener = (s: ConnState) => void;
const listeners = new Set<Listener>();
let state: ConnState = typeof navigator !== "undefined" && navigator.onLine ? "online" : "offline";
let pending = 0;

function emit() {
  for (const l of listeners) l(state);
}

export function getConnState(): ConnState {
  return state;
}

export function getPendingCount(): number {
  return pending;
}

export function setPendingCount(n: number) {
  pending = Math.max(0, n);
  // re-emit so badge updates
  emit();
}

export function setSyncing(syncing: boolean) {
  if (syncing) {
    if (state !== "offline") state = "syncing";
  } else {
    state = navigator.onLine ? "online" : "offline";
  }
  emit();
}

function handleOnline() {
  state = "online";
  emit();
  window.dispatchEvent(new CustomEvent("makai:reconnect"));
}
function handleOffline() {
  state = "offline";
  emit();
}

if (typeof window !== "undefined") {
  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);
}

/** Lightweight reachability probe in addition to navigator.onLine. */
export async function probeOnline(): Promise<boolean> {
  if (typeof navigator !== "undefined" && !navigator.onLine) return false;
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 3500);
    const r = await fetch("/favicon.ico?_ping=" + Date.now(), {
      method: "HEAD",
      cache: "no-store",
      signal: ctrl.signal,
    });
    clearTimeout(t);
    return r.ok || r.status === 304;
  } catch {
    return false;
  }
}

export function useConnection() {
  const [s, setS] = useState<ConnState>(state);
  const [p, setP] = useState<number>(pending);
  useEffect(() => {
    const l: Listener = (next) => {
      setS(next);
      setP(pending);
    };
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);
  return { state: s, pending: p };
}
